import { Link } from 'react-router-dom';
import {
  CubeIcon,
  LinkIcon,
  QuestionMarkCircleIcon,
  CreditCardIcon,
  LockClosedIcon,
  EnvelopeIcon,
  PhoneIcon,
  ClockIcon,
  ChatBubbleLeftIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { selectCategories, fetchCategoriesAsync } from '../product/productSlice';
import { useLanguage } from '../../context/LanguageContext';

function Footer() {
  const dispatch = useDispatch();
  const categories = useSelector(selectCategories);
  const { t } = useLanguage();

  useEffect(() => {
    if (categories.length === 0) {
      dispatch(fetchCategoriesAsync());
    }
  }, [dispatch, categories.length]);

  const navItems = [
    { name: 'home', link: '/home' },
    { name: 'about', link: '/about' },
    { name: 'products', link: '/' },
    { name: 'faq', link: '/faq' },
    { name: 'contact', link: '/contact' },
    { name: 'payments', link: '/payments' },
  ];

  return (
    <footer className="bg-white border-t border-blue-200">
      {/* Main Footer Content */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        {/* Header Section */}
        <div className="mb-14 pb-10 border-b border-blue-200">
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-slate-900 mb-3">
            SubifyHub
          </h2>
          <p className="text-slate-600 text-base sm:text-lg font-light leading-relaxed max-w-2xl">
            {t('premiumSubscriptions')}
          </p>
        </div>

        {/* Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16 mb-14">
          {/* Categories from Database */}
          <div>
            <div className="flex items-center gap-2.5 mb-5">
              <CubeIcon className="h-5 w-5 text-blue-600" />
              <h3 className="text-slate-900 font-medium text-base tracking-wide">
                {t('categories')}
              </h3>
            </div>
            <ul className="space-y-3">
              {categories && categories.length > 0 ? (
                categories.slice(0, 6).map((category) => (
                  <li key={category.id}>
                    <Link
                      to={`/?category=${category.label}`}
                      className="text-blue-600 hover:text-blue-600 transition-colors duration-200 text-sm"
                    >
                      {category.label}
                    </Link>
                  </li>
                ))
              ) : (
                <li className="text-slate-500 text-sm">Loading categories...</li>
              )}
            </ul>
          </div>

          {/* Useful Links with Navbar Items */}
          <div>
            <div className="flex items-center gap-2.5 mb-5">
              <LinkIcon className="h-5 w-5 text-blue-600" />
              <h3 className="text-slate-900 font-medium text-base tracking-wide">
                {t('quickLinks')}
              </h3>
            </div>
            <ul className="space-y-3">
              {navItems.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.link}
                    className="text-slate-600 hover:text-blue-600 transition-colors duration-200 text-sm"
                  >
                    {t(item.name)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Section */}
          <div>
            <div className="flex items-center gap-2.5 mb-5">
              <QuestionMarkCircleIcon className="h-5 w-5 text-blue-600" />
              <h3 className="text-slate-900 font-medium text-base tracking-wide">
                {t('support')}
              </h3>
            </div>
            <ul className="space-y-5">
              <li className="flex items-start gap-3">
                <EnvelopeIcon className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-slate-600 text-xs font-medium uppercase tracking-wider mb-1">
                    Email
                  </p>
                  <a
                    href="mailto:contact@subifyhub.com"
                    className="text-blue-600 hover:text-blue-600 transition-colors duration-200 text-sm"
                  >
                    contact@subifyhub.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <PhoneIcon className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-slate-600 text-xs font-medium uppercase tracking-wider mb-1">
                    WhatsApp
                  </p>
                  <a
                    href="https://wa.me/923272341839"
                    className="text-blue-600 hover:text-blue-600 transition-colors duration-200 text-sm"
                  >
                    +92 327 2341839
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <ChatBubbleLeftIcon className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-slate-600 text-xs font-medium uppercase tracking-wider mb-1">
                    {t('telegram')}
                  </p>
                  <a
                    href="https://t.me/subifyhub_support"
                    className="text-blue-600 hover:text-blue-600 transition-colors duration-200 text-sm"
                  >
                    +92 327 2341839
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <ClockIcon className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-slate-600 text-xs font-medium uppercase tracking-wider mb-1">
                    {t('hours')}
                  </p>
                  <p className="text-slate-400 text-sm">
                    {t('hours')}
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Sub-Footer Bar - White background as before */}
      <div className="bg-white border-t border-blue-200 py-6 px-4 sm:px-6 lg:px-8">
        <div className="w-full flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs">
          <div className="space-y-1.5 text-center md:text-left">
            <p className="text-slate-800 font-medium text-xs tracking-wide flex items-center justify-center md:justify-start gap-1.5">
              <MapPinIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <span>SubifyHub LLC — 1309 Coffeen Ave, Ste 1200, Sheridan, WY 82801, USA</span>
            </p>
            <p className="text-slate-500 text-[11px]">
              © 2025 SubifyHub LLC. {t('allRightsReserved')}
            </p>
          </div>

          <div className="flex items-center justify-center md:justify-end gap-3 flex-shrink-0">
            <span className="inline-flex items-center gap-1.5 text-xs bg-blue-50 border border-blue-200/60 px-3 py-1.5 rounded text-slate-700 font-medium">
              <CreditCardIcon className="h-3.5 w-3.5 text-blue-600" />
              {t('securePayments')}
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs bg-blue-50 border border-blue-200/60 px-3 py-1.5 rounded text-slate-700 font-medium">
              <LockClosedIcon className="h-3.5 w-3.5 text-blue-600" />
              {t('sslEncrypted')}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
